import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { Box, Button, FormControl, Input, Link, Typography } from "@mui/joy";
import React from "react";
import { useNavigate } from "react-router-dom";
import MissingInspectionNotesDialog from "../Dialogs/MissingInspectionNotesDialog";
import PortalTableLayout from "../components/PortalTableLayout";
import { Util } from "../Helpers/Util";

type SortColumn = "building" | "serial" | "lastInspection" | "status";
type SortDirection = "asc" | "desc";

interface MissingAnnualRow {
	elevators_id: string;
	buildings_id: string;
	building_name: string;
	serial_number: string;
	last_inspection_date: string | null;
	status: string;
	note_count: number;
}

const sortColumns: { key: SortColumn; label: string }[] = [
	{ key: "building", label: "Building" },
	{ key: "serial", label: "Serial Number" },
	{ key: "lastInspection", label: "Last Inspection" },
	{ key: "status", label: "Status" },
];

export default function MissingAnnualInspections() {
	const [dataset, setDataset] = React.useState([] as Array<MissingAnnualRow>);
	const [loading, setLoading] = React.useState(true);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
	const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc");
	const [notesModal, setNotesModal] = React.useState(false);
	const [selectedRow, setSelectedRow] = React.useState<MissingAnnualRow | null>(null);
	const n = useNavigate();

	React.useEffect(() => getData(), []);

	const getData = () => {
		setLoading(true);
		fetch("Inspection/GetMissingAnnualInspections", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => {
				setDataset(x);
				setLoading(false);
			});
	};

	const openNotesDialog = (row: MissingAnnualRow) => {
		setSelectedRow(row);
		setNotesModal(true);
	};

	const handleSort = (column: SortColumn) => {
		if (sortColumn === column) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortColumn(column);
			setSortDirection(column === "lastInspection" ? "desc" : "asc");
		}
	};

	const filteredData = dataset.filter((x) => {
		if (Util.isNullOrEmpty(globalFilter)) return true;
		const q = globalFilter.toLowerCase();
		const lastInspection = x.last_inspection_date ? Util.Formatter(x.last_inspection_date, "D") : "";
		return (
			(x.building_name ?? "").toLowerCase().includes(q) ||
			(x.serial_number ?? "").toLowerCase().includes(q) ||
			lastInspection?.toLowerCase().includes(q) ||
			(x.status ?? "").toLowerCase().includes(q)
		);
	});

	const sortedData = [...filteredData].sort((a, b) => {
		if (!sortColumn) return 0;
		let comparison = 0;
		switch (sortColumn) {
			case "building":
				comparison = (a.building_name ?? "").localeCompare(b.building_name ?? "", undefined, { numeric: true, sensitivity: "base" });
				break;
			case "serial":
				comparison = (a.serial_number ?? "").localeCompare(b.serial_number ?? "", undefined, { numeric: true, sensitivity: "base" });
				break;
			case "lastInspection": {
				const dateA = a.last_inspection_date ? new Date(a.last_inspection_date).getTime() : -Infinity;
				const dateB = b.last_inspection_date ? new Date(b.last_inspection_date).getTime() : -Infinity;
				comparison = dateA - dateB;
				break;
			}
			case "status":
				comparison = (a.status ?? "").localeCompare(b.status ?? "");
				break;
		}
		return sortDirection === "asc" ? comparison : -comparison;
	});

	return (
		<>
			<MissingInspectionNotesDialog
				open={notesModal}
				elevatorsId={selectedRow?.elevators_id ?? ""}
				buildingName={selectedRow?.building_name ?? ""}
				serialNumber={selectedRow?.serial_number ?? ""}
				onClose={() => setNotesModal(false)}
				onSaved={getData}
			/>
			<PortalTableLayout
				fillHeight={true}
				toolbar={
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "flex-start",
							justifyContent: "space-between",
							gap: 1.5,
							backgroundColor: "background.surface",
							border: "1px solid",
							borderColor: "divider",
							p: 1.5,
						}}>
						<Box>
							<Typography level="title-lg" sx={{ m: 0 }}>
								Missing / Expired Annual Inspections
							</Typography>
							<Typography level="body-sm" sx={{ mt: 0.5 }}>
								Active-building elevators with no annual inspection on file, or last annual inspection over 1 year old.
							</Typography>
						</Box>
						<FormControl sx={{ minWidth: 220 }}>
							<Input
								name="Search"
								placeholder="Global Search"
								value={globalFilter}
								onChange={(event) => setGlobalFilter(event.target.value)}
							/>
						</FormControl>
					</Box>
				}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{sortColumns.map((col) => (
								<TableCell key={col.key} sortDirection={sortColumn === col.key ? sortDirection : false}>
									<TableSortLabel
										active={sortColumn === col.key}
										direction={sortColumn === col.key ? sortDirection : "asc"}
										onClick={() => handleSort(col.key)}>
										{col.label}
									</TableSortLabel>
								</TableCell>
							))}
							<TableCell>Notes</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading && (
							<TableRow>
								<TableCell colSpan={5} align="center" sx={{ py: 3 }}>
									<CircularProgress size={32} />
								</TableCell>
							</TableRow>
						)}
						{!loading &&
							sortedData.map((x) => (
								<TableRow key={x.elevators_id} hover>
									<TableCell>
										<Link
											onClick={() => {
												n("/buildingportal/" + x.buildings_id);
											}}>
											{x.building_name}
										</Link>
									</TableCell>
									<TableCell>{x.serial_number}</TableCell>
									<TableCell>{x.last_inspection_date ? Util.Formatter(x.last_inspection_date, "D") : ""}</TableCell>
									<TableCell>{x.status}</TableCell>
									<TableCell>
										<Button size="sm" color="primary" startDecorator={x.note_count > 0 ? undefined : <AddIcon />} onClick={() => openNotesDialog(x)}>
											{x.note_count > 0 ? `Notes (${x.note_count})` : "Add Note"}
										</Button>
									</TableCell>
								</TableRow>
							))}
						{!loading && sortedData.length === 0 && (
							<TableRow>
								<TableCell colSpan={5} align="center">
									No results found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</>
	);
}
